import { useState, useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useConnectorDetails,
  useUpdateConnector,
  useUpdateCredentials,
  isConflictError,
  supportsCredentialEditing,
} from '@/hooks/useConnectors';
import { splitFormData } from '@/lib/validation/splitFormData';
import { buildZodSchema } from '@/lib/validation/buildConnectorSchema';
import { useConnectorTypeSchema } from '@/hooks/useConnectorTypes';
import { useToast } from '@/hooks/useToast';
import { JumpNavStrip } from './JumpNavStrip';
import { ExcludedAccountsSection } from './ExcludedAccountsSection';
import { ExpectedClientsSection } from './ExpectedClientsSection';
import { SectionHeader } from './SectionHeader';
import { SectionBody } from './SectionBody';
import { SaveBar } from './SaveBar';
import { ViewImpactDialog, viewBindingImpacts, type ViewImpact } from './ViewImpactDialog';
import { SECTIONS_BY_TYPE, defaultSections, sectionVisibleFields, type SectionSpec } from './sections';
import { countDirty, countDirtyBySection, isStoredValue } from './dirty';
import {
  EtagConflictDialog,
  SyncWarningDialog,
  UnsavedChangesDialog,
} from '@/components/connectors/shared';
import type { ConnectorType } from '@/types/connectors';
import type { Advertiser } from '@/components/connectors/form/LoadAdvertisersButton';
import { PASSWORD_MANAGER_OPT_OUT_CONTAINER } from '@/components/connectors/shared/passwordManagerOptOut';
import { DiscoveryGateProvider } from '@/lib/oauth/discoveryGate';

interface Props {
  connectorType: string;
  name: string;
}

export function SectionedConfigurationForm({ connectorType, name }: Props) {
  const { toast } = useToast();
  const details = useConnectorDetails(connectorType, name, true);
  const schemaQ = useConnectorTypeSchema(connectorType as ConnectorType);
  const updateConfig = useUpdateConnector();
  const updateCreds = useUpdateCredentials({ onConflict: () => setConflictOpen(true) });

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  // What each field showed before any edit -- an edit that returns to it is
  // no longer an edit, so it is neither counted nor sent (#2000).
  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [conflictOpen, setConflictOpen] = useState(false);
  const [syncWarnOpen, setSyncWarnOpen] = useState(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  // A save the server refused because it would orphan a published view (#2155).
  // Confirming resends it with acknowledge_view_impact; `force` carries the
  // refused attempt's ETag overwrite choice into the resend.
  const [viewImpact, setViewImpact] = useState<{ impacts: ViewImpact[]; force: boolean } | null>(null);

  // Runtime cache of results from LOAD_ADVERTISERS_BUTTON fields. Keyed by
  // the loader field's `name` (e.g. "load_advertisers"); companion
  // MULTI_SELECT_CHECKBOX_DYNAMIC fields read these via `loader_field`.
  // Mirrors DynamicConnectorForm so editor and wizard share one contract.
  const [loaderResults, setLoaderResults] = useState<Record<string, Advertiser[]>>({});

  const onLoaderResult = useCallback(
    (fieldName: string, advertisers: Advertiser[]) => {
      const selectedIds = new Set(
        (Array.isArray(values.account_ids) ? values.account_ids : []) as number[],
      );
      setLoaderResults((prev) => {
        const previous = prev[fieldName] ?? [];
        const freshIds = new Set(advertisers.map((a) => a.id));
        // Keep previously-known advertisers that are still selected but absent
        // from the fresh fetch, so a refresh never silently drops them. (#627)
        const preserved = previous.filter(
          (a) => selectedIds.has(a.id) && !freshIds.has(a.id),
        );
        return { ...prev, [fieldName]: [...advertisers, ...preserved] };
      });
    },
    [values.account_ids],
  );

  const connector = details.connector;
  const schema = schemaQ.data;

  const sections: SectionSpec[] = useMemo(() => {
    if (SECTIONS_BY_TYPE[connectorType]) return SECTIONS_BY_TYPE[connectorType];
    return schema ? defaultSections(schema) : [];
  }, [connectorType, schema]);

  // Sections with at least one field visible under the current form values.
  // Fully visible_if-gated sections (e.g. Google Sheets Drive discovery in
  // single-spreadsheet mode) are hidden, mirroring the wizard dropping empty
  // groups. The full `sections` list is still used for save/dirty logic below
  // so values for currently-hidden fields are never lost.
  const visibleSections: SectionSpec[] = useMemo(() => {
    if (!schema) return [];
    return sections.filter(
      (s) => sectionVisibleFields(s, schema.fields, values).length > 0,
    );
  }, [sections, schema, values]);

  // #1163 — client-side validation on the edit path.
  //
  // Scope is `dirty ∩ visible`, NOT the whole form. That is the exact set the
  // save transmits, and the exact client-side mirror of the server's
  // `check_settings_patch` "no-worse-than" rule (settings_validation.py): a
  // config whose stored settings are already invalid stays editable. A
  // whole-form parse would instead block every Save on this surface, because a
  // stored credential renders blank and `required` would fail on it.
  //
  // The visible set comes from `sectionVisibleFields` — the same predicate
  // SectionBody uses to decide what to draw — so we can never error on a field
  // the operator cannot see. Synthetic fields have no schema entry
  // and are excluded.
  const validatedFields = useMemo(() => {
    const names = new Set<string>();
    if (!schema) return names;
    for (const section of visibleSections) {
      for (const name of sectionVisibleFields(section, schema.fields, values)) {
        if (section.syntheticFields?.[name]) continue;
        if (dirty[name]) names.add(name);
      }
    }
    return names;
  }, [visibleSections, schema, values, dirty]);

  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    if (!schema || validatedFields.size === 0) return out;
    const result = buildZodSchema(schema.fields, validatedFields).safeParse(values);
    if (result.success) return out;
    // First issue per field, matching wizardValidation.ts.
    for (const issue of result.error.issues) {
      const name = String(issue.path[0] ?? '');
      if (name && validatedFields.has(name) && !out[name]) out[name] = issue.message;
    }
    return out;
  }, [schema, validatedFields, values]);

  const invalidCount = Object.keys(errors).length;

  // `active` is the user's section-nav selection; it's independent local state
  // (set via JumpNavStrip's onActivate below). When `visibleSections` changes
  // (schema load, or a visible_if gate flipping) and the current `active` id
  // is no longer among them, fall back to the first visible section. This is
  // a render-time derivation (React's "adjusting state when a prop changes"
  // pattern, https://react.dev/learn/you-might-not-need-an-effect) rather than
  // an effect, so there's no extra render/flash of a stale active id.
  const [active, setActive] = useState('');
  const effectiveActive =
    visibleSections.some((s) => s.id === active)
      ? active
      : (visibleSections[0]?.id ?? '');

  // Seed form values from connector details.
  //
  // This mirrors external data (connector/details/schema) into locally-edited
  // state (`values`), merging in the user's in-flight dirty edits — it can't be
  // a pure render-time useMemo because `values` is also written directly by
  // onChange/onBatchFieldChange. Instead, follow React's "adjusting state when
  // a prop changes" pattern: track the last-seeded inputs in state and, when
  // they change, seed synchronously during render (not in an effect) so there's
  // no extra render/flash of stale values.
  const [seededFrom, setSeededFrom] = useState<{
    connector: typeof connector;
    detailsData: unknown;
    schema: typeof schema;
  } | null>(null);
  if (
    connector &&
    (seededFrom === null ||
      seededFrom.connector !== connector ||
      seededFrom.detailsData !== details.data ||
      seededFrom.schema !== schema)
  ) {
    // Seed order:
    //   1. Schema defaults (lowest priority) so fields never persisted on the
    //      connector still render as their declared default — matches the
    //      wizard's DynamicConnectorForm seed effect. Without this, older
    //      connectors or wizard regressions leave the editor showing every
    //      extraction field blank even though the schema has sensible defaults.
    //   2. Connection (credential store, sensitive values nullified) — keys
    //      that share names with settings still get overwritten in step 3.
    //   3. config.settings (highest priority) — authoritative for fields the
    //      editor saves (customer_id, resources, etc.). Inverting 2 and 3
    //      would let stale connection values silently win over freshly-saved
    //      settings.
    const schemaDefaults: Record<string, unknown> = {};
    for (const f of schema?.fields ?? []) {
      const def = (f as { default?: unknown }).default;
      if (def === undefined || def === null) continue;
      schemaDefaults[f.name] = def;
    }
    const seeded: Record<string, unknown> = {
      ...schemaDefaults,
      ...((details.data as any)?.connection || {}),
      ...(connector.config?.settings || {}),
    };
    // No section renders a `queries` field, so a raw `settings.queries` key
    // must never enter the form values. (#968)
    delete seeded.queries;
    // Field-level merge, dirty-aware. This re-seeds on every background poll
    // (the run-status refetch hands `details.data` a fresh reference every
    // ~5s during a run). A wholesale `setValues(seeded)` + `setDirty({})` here
    // clobbers the user's unsaved edits mid-typing. Instead: adopt fresh server
    // values only for UNTOUCHED (clean) fields, and keep every DIRTY field's
    // edited value. Dirty flags are preserved (no `setDirty({})`) so a poll
    // cannot silently discard which fields still await saving — first load
    // starts clean, and onSave/onDiscard clear the dirty map explicitly. (#967)
    setSeededFrom({ connector, detailsData: details.data, schema });
    setBaseline(seeded);
    setValues((prev) => {
      const merged: Record<string, unknown> = { ...seeded };
      for (const key in dirty) {
        if (dirty[key]) merged[key] = prev[key];
      }
      return merged;
    });
  }

  // Seed the advertiser loader cache from persisted selected_advertisers so the
  // Configuration page renders saved advertisers by name without a live fetch.
  // Keyed by the loader field name ("load_advertisers") to match the companion
  // account_ids field's loader_field pointer. (#627)
  // Render-time seed (see above) keyed on `connector` identity, matching the
  // effect's prior dependency array.
  const [advertisersSeededFrom, setAdvertisersSeededFrom] = useState<typeof connector>(undefined);
  if (connector !== advertisersSeededFrom) {
    setAdvertisersSeededFrom(connector);
    const saved = (connector?.config?.settings as Record<string, unknown> | undefined)
      ?.selected_advertisers;
    if (Array.isArray(saved) && saved.length > 0) {
      const seeded = saved
        .filter((a): a is { id: number; name: string; status?: string } =>
          !!a && typeof (a as { id?: unknown }).id === 'number')
        .map((a) => ({ id: a.id, name: a.name ?? String(a.id), status: a.status ?? '' }));
      setLoaderResults((prev) =>
        'load_advertisers' in prev ? prev : { ...prev, load_advertisers: seeded });
    }
  }

  const onChange = useCallback((field: string, value: unknown) => {
    setValues((v) => ({ ...v, [field]: value }));
    setDirty((d) => ({ ...d, [field]: !isStoredValue(value, baseline[field]) }));
  }, [setValues, setDirty, baseline]);

  const onBatchFieldChange = useCallback((updates: Record<string, unknown>) => {
    setValues((v) => ({ ...v, ...updates }));
    setDirty((d) => {
      const next = { ...d };
      for (const k of Object.keys(updates)) next[k] = true;
      return next;
    });
  }, [setValues, setDirty]);

  const onDiscard = useCallback(() => {
    if (!connector) return;
    const seeded: Record<string, unknown> = {
      ...((details.data as any)?.connection || {}),
      ...(connector.config?.settings || {}),
    };
    // Never seed a raw `settings.queries` key into the form values. (#968)
    delete seeded.queries;
    setValues(seeded);
    setDirty({});
  }, [connector, details.data, setValues, setDirty]);

  const onSave = useCallback(async (forceOverwrite = false, acknowledgeViewImpact = false) => {
    if (!connector || !schema) return;

    // The SaveBar is disabled while invalid, but UnsavedChangesDialog's Save and
    // EtagConflictDialog's Overwrite call onSave directly — guard here so the
    // gate is real rather than cosmetic. (#1163)
    if (Object.keys(errors).length > 0) return;

    const sectionFields = sections.flatMap((s) => s.fields);
    const dirtyValues: Record<string, unknown> = {};
    for (const f of sectionFields) {
      if (dirty[f]) dirtyValues[f] = values[f];
    }

    // Synthetic-field translators (e.g. date_range → connector-specific keys)
    for (const s of sections) {
      if (!s.translators) continue;
      for (const fname of s.fields) {
        const t = s.translators[fname];
        if (!t || !dirty[fname]) continue;
        Object.assign(dirtyValues, t(values[fname]));
        delete dirtyValues[fname];
      }
    }

    const { credentials, settings } = splitFormData(dirtyValues, schema.fields);

    // #1990: for a select_dynamic, blank is a CHOICE ("every advertiser on the
    // account"), not an unset control -- but splitFormData drops '' as empty,
    // so clearing a saved filter sent nothing and the save silently kept it
    // (config 28, 2026-09-22). Send an explicit null: the PATCH shallow-merges
    // settings, and the settings model reads null as "no filter".
    for (const f of schema.fields) {
      if (f.type === 'select_dynamic' && dirty[f.name] && dirtyValues[f.name] === '') {
        settings[f.name] = null;
      }
    }

    // Persist advertiser display names alongside account_ids whenever the
    // selection changed, derived from the live chip options. (#627)
    // Guard: only connectors whose schema declares `selected_advertisers` get
    // this treatment — other connectors (bing_ads, linkedin_ads, twitter_ads,
    // reddit_ads) also have account_ids but must not have a synthetic
    // selected_advertisers injected into their config patch.
    const hasSelectedAdvertisers = schema.fields.some((f) => f.name === 'selected_advertisers');
    if (dirty.account_ids && hasSelectedAdvertisers) {
      const ids = (Array.isArray(values.account_ids) ? values.account_ids : []) as number[];
      const opts = loaderResults.load_advertisers ?? [];
      const byId = new Map(opts.map((o) => [o.id, o]));
      settings.selected_advertisers = ids.map((id) => {
        const o = byId.get(id);
        return { id, name: o?.name ?? String(id), status: o?.status ?? '' };
      });
    }

    // Strip oauth_button fields — those are managed by the OAuth completion endpoint,
    // not via the editor's save flow. Their dirty state still clears at save end.
    for (const f of schema.fields) {
      if (f.type === 'oauth_button') {
        delete (credentials as Record<string, unknown>)[f.name];
        delete settings[f.name];
      }
    }

    // Not all connectors expose a PATCH /credentials route. For connectors that
    // don't (everything except google_ads, s3_csv and vistar today), fold any credential-typed
    // fields into settings so they're persisted via the config patch.
    const canEditCredentials = supportsCredentialEditing(connectorType);
    if (!canEditCredentials) {
      Object.assign(settings, credentials);
      for (const k of Object.keys(credentials)) delete (credentials as Record<string, unknown>)[k];
    }

    // No section renders `queries`; never transmit a stray key.
    if ('queries' in settings) delete settings.queries;
    if ('queries' in credentials) delete (credentials as Record<string, unknown>).queries;

    try {
      const configPatch: Record<string, unknown> = {};
      if (Object.keys(settings).length > 0) configPatch.settings = settings;
      let freshEtag = details.etag;
      if (Object.keys(configPatch).length > 0) {
        // Only ever set from the ViewImpactDialog's confirm (#2155).
        if (acknowledgeViewImpact) configPatch.acknowledge_view_impact = true;
        try {
          const r = await updateConfig.mutateAsync({
            connectorType: connectorType as ConnectorType,
            name, config: configPatch,
          });
          freshEtag = (r as any)?.etag ?? freshEtag;
        } catch (err) {
          // #2155: the view-binding 409 asks the operator instead of failing.
          // Nothing was written, so the edits stay dirty until they choose.
          const impacts = acknowledgeViewImpact ? null : viewBindingImpacts(err);
          if (impacts) {
            setViewImpact({ impacts, force: forceOverwrite });
            return;
          }
          throw err;
        }
      }

      if (canEditCredentials && Object.keys(credentials).length > 0) {
        try {
          await updateCreds.mutateAsync({
            connectorType: connectorType as ConnectorType,
            name, credentials: credentials as any,
            etag: forceOverwrite ? undefined : freshEtag,
          });
        } catch (err) {
          if (isConflictError(err)) return;
          throw err;
        }
      }

      toast({ title: 'Configuration Updated', description: `${name} configuration has been saved successfully.` });
      setDirty({});
    } catch (err) {
      toast({
        title: 'Failed to Save',
        description: err instanceof Error ? err.message : 'Failed to update connector configuration',
        variant: 'destructive',
      });
    }
  }, [
    connector, schema, sections, dirty, values, errors, loaderResults,
    details, connectorType, name,
    updateConfig, updateCreds, toast, setDirty,
  ]);

  const dirtyBySection = useMemo(
    () => countDirtyBySection(sections, dirty),
    [sections, dirty],
  );
  const totalDirty = countDirty(dirty);
  const isSaving =
    updateConfig.isPending ||
    updateCreds.isPending;

  if (details.isLoading || schemaQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (details.error || !connector || !schema) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load connector details:{' '}
          {details.error instanceof Error
            ? details.error.message
            : 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <DiscoveryGateProvider>
    <div className="rounded-lg border bg-card" {...PASSWORD_MANAGER_OPT_OUT_CONTAINER}>
      <JumpNavStrip
        sections={visibleSections}
        activeId={effectiveActive}
        dirtyBySection={dirtyBySection}
        onActivate={(id) => {
          setActive(id);
          document
            .getElementById(`section-${id}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      <div className="px-5 pt-1 pb-5">
        {visibleSections.map((s, i) => (
          <section
            key={s.id}
            id={`section-${s.id}`}
            className={i < visibleSections.length - 1 ? 'border-b py-6' : 'py-6'}
          >
            <SectionHeader
              icon={s.icon}
              title={s.title}
              description={s.description}
              dirtyCount={dirtyBySection[s.id] ?? 0}
            />
            <SectionBody
              section={s}
              schemaFields={schema.fields}
              values={values}
              dirty={dirty}
              errors={errors}
              credentialFlags={
                (details.credentialFlags ?? {}) as Record<string, boolean>
              }
              connectorType={connectorType}
              connectorName={name}
              disabled={isSaving}
              onChange={onChange}
              onBatchFieldChange={onBatchFieldChange}
              oauthIdentity={details.oauthIdentity}
              onReconnected={() => details.refetch?.()}
              loaderResults={loaderResults}
              onLoaderResult={onLoaderResult}
            />
          </section>
        ))}

        <ExcludedAccountsSection
          schemaFields={schema.fields}
          settings={(connector.config?.settings ?? {}) as Record<string, unknown>}
          connectorType={connectorType}
          connectorName={name}
        />

        {connectorType === 'google_ads' && (
          <ExpectedClientsSection
            connectorType={connectorType}
            name={name}
            settings={(connector.config?.settings ?? {}) as Record<string, unknown>}
          />
        )}

        <SaveBar
          dirtyCount={totalDirty}
          invalidCount={invalidCount}
          isSaving={isSaving}
          onSave={() => onSave()}
          onDiscard={onDiscard}
        />
      </div>

      <ViewImpactDialog
        impacts={viewImpact?.impacts ?? null}
        onConfirm={() => {
          const force = viewImpact?.force ?? false;
          setViewImpact(null);
          onSave(force, true);
        }}
        onCancel={() => setViewImpact(null)}
      />
      <EtagConflictDialog
        isOpen={conflictOpen}
        onReload={async () => {
          setConflictOpen(false);
          await details.refetch?.();
        }}
        onOverwrite={() => { setConflictOpen(false); onSave(true); }}
        onCancel={() => setConflictOpen(false)}
      />
      <SyncWarningDialog
        isOpen={syncWarnOpen}
        connectorName={connector.name}
        onClose={() => setSyncWarnOpen(false)}
        onConfirm={() => setSyncWarnOpen(false)}
      />
      <UnsavedChangesDialog
        isOpen={unsavedOpen}
        onDiscard={() => {
          setUnsavedOpen(false);
          onDiscard();
        }}
        onSave={() => {
          setUnsavedOpen(false);
          onSave();
        }}
        onCancel={() => setUnsavedOpen(false)}
      />
    </div>
    </DiscoveryGateProvider>
  );
}
